import { Button, Flex, useToast } from '@chakra-ui/react'
import { format, getTime, isFuture } from 'date-fns'
import { forwardRef, useEffect } from 'react'
import { StyledDatepicker } from '../common'
import {
  calendarMaxDate, getOpenDateTimeForDate, isOpenDayForLocation,
  isValidPickupDateTimeForLocation, nextValidPickupTime,
  tsPickupUpdate, tsPickupUpdateDate,
} from '../states/pickupActions'
import { getLocation } from '../states/prefsActions'
import { useStore } from '../states/useStore'

import { roundUpMins } from '../common/utils'

const Datepicker = props => (
  <StyledDatepicker w='full' h='8' fontSize='md' {...props} />
)

const PickerButton = forwardRef(({ value, onClick }, ref) => (
  <Button variant='outline' onClick={onClick} ref={ref}>
    {value}
  </Button>
))

const PickupTime = () => {
  const toast = useToast()

  const locId = useStore(state => state.locId)
  const location = getLocation(locId)

  const tsPickup = useStore(state => state.tsPickup)

  const [dtOpen, dtClose] = getOpenDateTimeForDate(location)(Date.now())

  const handleNow = (loc) => e => {
    const now = Date.now()

    toast.closeAll()
    if (!isOpenDayForLocation(loc)(now)) {
      toast({
        title: `${format(now, 'iiii, d/MM')}: Outlet is not open today`,
        status: 'warning',
        duration: 5000,
        isClosable: true,
        position: 'top-right'
      })
      return
    }
    if (getTime(now) < getTime(dtOpen)) {
      toast({
        title: `${format(now, 'h:mm aa')}: Outlet is not open yet`,
        status: 'warning',
        duration: 5000,
        isClosable: true,
        position: 'top-right'
      })
      return
    }
    if (getTime(now) > getTime(dtClose)) {
      toast({
        title: `${format(now, 'h:mm aa')}: Outlet is closed`,
        status: 'warning',
        duration: 5000,
        isClosable: true,
        position: 'top-right'
      })
      return
    }

    tsPickupUpdate(roundUpMins(now))
  }

  useEffect(() => {
    if (!location) return

    if (!tsPickup) {
      // first visit: default to the earliest slot that's actually valid
      // right now — never "today's opening time" regardless of the actual
      // current time, which could already be hours in the past.
      tsPickupUpdate(roundUpMins(nextValidPickupTime(location)))
      return
    }

    // a previously-valid tsPickup (persisted across visits) can go stale —
    // the day closed, hours changed, or time simply passed. Catch that
    // proactively here instead of only failing silently at checkout.
    const check = isValidPickupDateTimeForLocation(tsPickup, location)
    if (check instanceof Error) {
      tsPickupUpdate(roundUpMins(nextValidPickupTime(location)))
      toast({
        title: 'Your pickup time was no longer available — we picked the next available time for you.',
        status: 'info',
        duration: 6000,
        isClosable: true,
        position: 'top-right',
      })
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location, tsPickup])

  return (
    <Flex w='full' alignItems='center' justifyContent='space-between'>
      <Datepicker
        w='9rem'
        selected={tsPickup || roundUpMins(nextValidPickupTime(location) || Date.now())}
        onChange={tsPickupUpdateDate}
        filterDate={isOpenDayForLocation(location)}
        minDate={Date.now()}
        maxDate={calendarMaxDate(Date.now())} // 1 + 1 months max
        dateFormat='iiii, d/MM'
        customInput={<PickerButton />}
      />
      <Datepicker
        mx='1'
        w='5rem'
        selected={tsPickup || roundUpMins(nextValidPickupTime(location) || Date.now())}
        onChange={tsPickupUpdate}
        showTimeSelect
        showTimeSelectOnly
        timeIntervals={10}
        minTime={dtOpen}
        maxTime={dtClose}
        filterDate={isFuture}
        timeCaption="Time"
        timeFormat="HH:mm"
        dateFormat="HH:mm"
        customInput={<PickerButton />}
      />
      <Button mr='2px' ml='0' variant='link' color='blue' onClick={handleNow(location)}>Now</Button>
    </Flex>
  )
}

export default PickupTime